import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Empty, Input, Message, Modal, Select, Space, Table, Typography } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, DeleteApi } from "../Api/AuthenticatedApi";
import CustomFieldInputs, { missingRequiredFields, formatErrors } from "../Components/CustomFields/CustomFieldInputs";
import useRecordTypes from "../Components/CustomFields/useRecordTypes";

const { Text } = Typography;

export default function Companies() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [customFields, setCustomFields] = useState({});
  const [cfDefs, setCfDefs] = useState([]);
  const [typeId, setTypeId] = useState("");
  const { types, defaultTypeId } = useRecordTypes("company");
  const [saving, setSaving] = useState(false);

  const fetchCompanies = () => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, "/api/companies/",
      (data) => { setRows(data || []); setLoading(false); },
      () => navigate("/login"),
      () => { setLoading(false); Message.error("Failed to load companies"); });
  };

  useEffect(fetchCompanies, [getAccessTokenSilently, navigate]);

  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=company",
      (data) => setCfDefs(data || []), () => {}, () => {});
  }, [getAccessTokenSilently]);

  const create = () => {
    if (!name.trim()) { Message.warning("Name the company"); return; }
    const missing = missingRequiredFields(cfDefs, customFields, typeId);
    if (missing.length) { Message.warning("Required: " + missing.join(", ")); return; }
    const badFormat = formatErrors(cfDefs, customFields, typeId);
    if (badFormat.length) { Message.warning(badFormat.join("; ")); return; }
    setSaving(true);
    PostApi(getAccessTokenSilently, "/api/companies/",
      (created) => {
        setSaving(false); setModalOpen(false); setName(""); setDomain(""); setCustomFields({});
        Message.success("Company created");
        if (created && created.id) navigate(`/companies/${created.id}`);
        else fetchCompanies();
      },
      () => navigate("/login"),
      (err) => { setSaving(false); Message.error((err && err.message) || "Failed to create company"); },
      { name: name.trim(), domain: domain.trim(), record_type_id: typeId || null, custom_fields: customFields });
  };

  const remove = (e, c) => {
    e.stopPropagation();
    Modal.confirm({
      title: "Delete company",
      content: `Delete "${c.name}"? Its contacts are kept — they're just unlinked.`,
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve) => {
        DeleteApi(getAccessTokenSilently, "/api/companies/" + c.id,
          () => { Message.success("Company deleted"); fetchCompanies(); resolve(); },
          () => navigate("/login"),
          () => { Message.error("Failed to delete company"); resolve(); },
          {});
      }),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", render: (n) => <Text style={{ fontWeight: 500 }}>{n}</Text> },
    { title: "Domain", dataIndex: "domain", render: (d) => d || <Text type="secondary">—</Text> },
    { title: "Contacts", dataIndex: "contact_count" },
    {
      title: "",
      width: 48,
      render: (_, c) => <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={(e) => remove(e, c)} />,
    },
  ];

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Companies" }]} />

      <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 8px" }}>
        <Button type="primary" icon={<IconPlus />} onClick={() => { setName(""); setDomain(""); setCustomFields({}); setTypeId(defaultTypeId || ""); setModalOpen(true); }}>New company</Button>
      </div>

      <SummaryCard title="Companies" subTitle="Group contacts by the business they belong to">
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          data={rows}
          loading={loading}
          pagination={false}
          noDataElement={
            <Empty description="No companies yet">
              <Button type="primary" style={{ marginTop: 12 }} onClick={() => { setName(""); setDomain(""); setCustomFields({}); setTypeId(defaultTypeId || ""); setModalOpen(true); }}>Create your first company</Button>
            </Empty>
          }
          onRow={(row) => ({ style: { cursor: "pointer" }, onClick: () => navigate(`/companies/${row.id}`) })}
        />
      </SummaryCard>

      <Modal
        title="New company"
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={create}>Create</Button>
          </Space>
        }
        unmountOnExit
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
            <Input placeholder="e.g. Acme Corp" value={name} onChange={setName} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Domain</Text></div>
            <Input placeholder="e.g. acme.com" value={domain} onChange={setDomain} />
          </div>
          {types.length > 0 && (
            <div>
              <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Type</Text></div>
              <Select
                allowClear
                placeholder="No type"
                style={{ width: "100%" }}
                value={typeId || undefined}
                onChange={(v) => setTypeId(v || "")}
                options={types.map((t) => ({ label: t.name, value: t.id }))}
              />
            </div>
          )}
          <CustomFieldInputs definitions={cfDefs} values={customFields} typeId={typeId} onChange={setCustomFields} />
        </Space>
      </Modal>
    </Content>
  );
}
